import React, {useEffect, useState} from "react";
import moment from "moment/moment";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_JOB_STEP_RUNS} from "../graphql/queries/gqlGetJobStepRun";
import {
	Alert,
	AlertTitle,
	Box,
	ButtonGroup, CircularProgress,
	IconButton,
	Typography,
} from "@mui/material";
import {Refresh} from "@mui/icons-material";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {ConfirmationDialog} from "../../../../components/common/confirmation-dialog/ConfirmationDialog";
import {toast} from "react-toastify";
import {CANCEL_JOB_RUN} from "../graphql/mutations/gqlCancelJobStepRun";
import {RERUN_JOB_STEP_RUN} from "../graphql/mutations/gqlReRunJobStepRun";
import {IJobStepRun} from "../../models/JobStepRun";
import {NEW_RUN_FROM_EXISTING_STEP} from "../graphql/mutations/gqlNewRunFromExistingStep";

interface IJobStepRunLogProps {
	jobRunId: number;
	jobStepRunId: number;
}

export const JobStepRunGeneral: React.FC<IJobStepRunLogProps> = (props) => {
	const [data, setData] = useState<IJobStepRun>();
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);

	const fetch = async () => {
		setSubmitLoading(true);
		const result = await graphQLServices.querytoolClient.query({
			query: GET_JOB_STEP_RUNS,
			variables: {
				jobRunId: props.jobRunId,
				jobRunStepId: props.jobStepRunId,
			},
		});

		setData(result?.data?.jobStepRuns?.nodes[0]);
		setSubmitLoading(false);
	};

	const cancelStep = () => {
		setSubmitLoading(true);

		graphQLServices.querytoolClient
			.mutate({
				mutation: CANCEL_JOB_RUN,
				variables: {
					jobStepRunId: props.jobStepRunId,
				},
			})
			.then((result) => {
				const success = result.data.cancelJobStepRun.success;
				if (success) {
					toast.success("Cancelled");
				} else {
					toast.error(
						result.data.cancelJobStepRun?.errors[0]?.message ?? "Failed"
					);
				}
			})
			.finally(() => {
				setSubmitLoading(false);
				fetch();
			});
	};
	
	const startNewRunFromExistingStep = () => {
		setSubmitLoading(true);
		
		graphQLServices.querytoolClient
			.mutate({
				mutation: NEW_RUN_FROM_EXISTING_STEP,
				variables: {
					jobRunId: props.jobRunId,
					jobStepRunId: props.jobStepRunId,
				},
			})
			.then((result) => {
				console.log(result);
				const success = result.data?.newRunFromExistingStep?.jobRun?.id > 0;
				if (success) {
					toast.success("New Run Started");
				} else {
					toast.error(
						result.data?.newRunFromExistingStep?.errors[0]?.message ?? "Failed"
					);
				}
			})
			.finally(() => {
				setSubmitLoading(false);
				fetch();
			});
	};
	
	const reRunStep = (failedOnly: boolean) => {
		setSubmitLoading(true);
		
		graphQLServices.querytoolClient
			.mutate({
				mutation: RERUN_JOB_STEP_RUN,
				variables: {
					jobStepRunId: props.jobStepRunId,
					failedOnly: failedOnly
				},
			})
			.then((result) => {
				const success = result.data.reRunJobStepRun?.success;
				if (success) {
					toast.success("ReRun Started");
				} else {
					toast.error(
						result.data.errors[0]?.message ?? "Failed"
					);
				}
			})
			.finally(() => {
				setSubmitLoading(false);
				fetch();
			});
	};

	useEffect(() => {
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{submitLoading && <CircularProgress />}
			{!submitLoading && data && (
				<>
					<div>
						<IconButton onClick={() => fetch()}>
							<Refresh />
						</IconButton>
					</div>

					<Table>
						{data.start && <TableRow>
							<TableCell>Started:</TableCell>
							<TableCell>
								<Typography variant="caption" component="h2">
									{moment(data.start).format("DD-MM-YYYY HH:mm:ss")}
								</Typography>
							</TableCell>
						</TableRow>}
						{data.cancel && <TableRow>
							<TableCell>
								<Typography variant="h3" component="h2">
									Cancelled
								</Typography>
							</TableCell>
						</TableRow>}
						{data.finish && <TableRow>
							<TableCell>Finished:</TableCell>
							<TableCell>
								<Typography variant="caption" component="h2">
									{data.finish &&
										moment(data.finish).format("DD-MM-YYYY HH:mm:ss")}
								</Typography>
							</TableCell>
						</TableRow>}
					</Table>

					{data.start && (
						<>
							<Alert severity="success">
								<AlertTitle>Succeeded</AlertTitle>
								{data?.succeeded}
							</Alert>
							<Alert severity="error">
								<AlertTitle>Failed</AlertTitle>
								{data?.failed}
							</Alert>
							<br />
							<Alert severity="info">
								<AlertTitle>Total</AlertTitle>
								{data?.total}
							</Alert>
						</>
					)}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							"& > *": {
								m: 1,
							},
						}}
					>
						<ButtonGroup orientation={"horizontal"}>
							{!data.cancel && data.start && !data.finish && data.total + data.failed + data.succeeded > 0  && (
								<ConfirmationDialog
									onSuccess={cancelStep}
									dialogTitle={"Are you sure?"}
									buttonTitle={"Cancel"}
									buttonProps={{ variant: "outlined" }}
									dialogDescription={
										"This will stop the execution of this step"
									}
								/>
							)}
							{(data.finish || data.cancel) && data.total + data.failed + data.succeeded > 0 && (
								<ConfirmationDialog
									onSuccess={() => reRunStep(false)}
									dialogTitle={"Are you sure?"}
									buttonTitle={"Restart all"}
									buttonProps={{ variant: "outlined" }}
									dialogDescription={
										"This will restart this step where all records are processed again."
									}
								/>
							)}
							{data.finish && data.failed > 0 && ( <>
								<ConfirmationDialog
									onSuccess={() => reRunStep(true)}
									dialogTitle={"Are you sure?"}
									buttonTitle={"Retry failed"}
									buttonProps={{ variant: "outlined" }}
									dialogDescription={
										"This will restart this step where only the failed records are processed again."
									}
								/>
								<ConfirmationDialog
									onSuccess={startNewRunFromExistingStep}
									dialogTitle={"Are you sure?"}
									buttonTitle={"Start New Run From Failed"}
									buttonProps={{ variant: "outlined" }}
									dialogDescription={
										"This will create and start a new run with the failed items of this run."
									}
								/>
							</>
							)}
						</ButtonGroup>
					</Box>
				</>
			)}
		</>
	);
};
